/**
 * Collection/search facets: AJAX section refresh on filter or sort change.
 *
 * Sort UI is split (mobile sidebar vs desktop toolbar) but shares one logical form:
 * - #FacetFiltersForm lives in the sidebar (filters + mobile sort)
 * - Desktop sort radios use form="FacetFiltersForm" (HTML5 association)
 * - [data-facets-sort-by] hooks keep both UIs in sync after each fetch
 * - Sidebar/mobile: name="sort_by" | Desktop: name="sort_by_desktop" (separate radio groups)
 */
class FacetFiltersForm extends HTMLElement {
	static SORT_INPUT_NAME = {
		sidebar: 'sort_by',
		desktop: 'sort_by_desktop',
	};

	static getActiveSortPlacement() {
		return window.matchMedia('(min-width: 1024px)').matches
			? 'desktop'
			: 'sidebar';
	}

	static getSortInputName(placement) {
		return FacetFiltersForm.SORT_INPUT_NAME[placement] ?? 'sort_by';
	}

	static getCheckedSortValue() {
		const placement = FacetFiltersForm.getActiveSortPlacement();
		const inputName = FacetFiltersForm.getSortInputName(placement);
		const checked = document.querySelector(
			`[data-facets-sort-by="${placement}"] input[name="${inputName}"]:checked`,
		);

		return checked?.value ?? null;
	}
	constructor() {
		super();
		this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
		this.onFacetsControlEvent = this.onFacetsControlEvent.bind(this);
		this.onSortViewportChange = this.onSortViewportChange.bind(this);

		this.debouncedOnSubmit = debounce((event) => {
			this.onSubmitHandler(event);
		}, 500);

		// Delegated listeners: sidebar fields + desktop sort (form= attribute)
		this.addEventListener('input', this.onFacetsControlEvent);
		this.addEventListener('change', this.onFacetsControlEvent);

		this.sortViewportQuery = window.matchMedia('(min-width: 1024px)');
		this.sortViewportQuery.addEventListener('change', this.onSortViewportChange);
		FacetFiltersForm.syncSortControlsState();
	}

	disconnectedCallback() {
		this.sortViewportQuery?.removeEventListener(
			'change',
			this.onSortViewportChange,
		);
	}

	onSortViewportChange() {
		FacetFiltersForm.syncSortControlsState();
	}

	onFacetsControlEvent(event) {
		const facetForm = this.querySelector('[data-facets-form]');
		if (!facetForm?.id || !this.isFacetFormControl(event.target, facetForm)) {
			return;
		}

		this.debouncedOnSubmit(event);
	}

	// Field belongs to FacetFiltersForm (in-DOM or linked via form="FacetFiltersForm")
	isFacetFormControl(target, facetForm) {
		if (!target?.name) return false;

		const isSortControl = Object.values(FacetFiltersForm.SORT_INPUT_NAME).includes(
			target.name,
		);

		return (
			isSortControl ||
			facetForm.contains(target) ||
			target.getAttribute('form') === facetForm.id
		);
	}

	static setListeners() {
		const onHistoryChange = (event) => {
			const searchParams = event.state
				? event.state.searchParams
				: FacetFiltersForm.searchParamsInitial;
			if (searchParams === FacetFiltersForm.searchParamsPrev) return;
			FacetFiltersForm.renderPage(searchParams, null, false);
		};
		window.addEventListener('popstate', onHistoryChange);
	}

	static toggleActiveFacets(disable = true) {
		document.querySelectorAll('[data-facets-remove]').forEach((element) => {
			element.classList.toggle('disabled', disable);
		});
	}

	static renderPage(searchParams, event, updateURLHash = true) {
		FacetFiltersForm.searchParamsPrev = searchParams;
		const sections = FacetFiltersForm.getSections();

		sections.forEach((section) => {
			const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
			const filterDataUrl = (element) => element.url === url;

			FacetFiltersForm.filterData.some(filterDataUrl)
				? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
				: FacetFiltersForm.renderSectionFromFetch(url, event);
		});

		if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
	}

	static renderSectionFromFetch(url, event) {
		fetch(url)
			.then((response) => response.text())
			.then((responseText) => {
				const html = responseText;
				FacetFiltersForm.filterData = [
					...FacetFiltersForm.filterData,
					{html, url},
				];
				FacetFiltersForm.renderFilters(html, event);
				FacetFiltersForm.renderSortBy(html);
				FacetFiltersForm.renderProductGridContainer(html);
				FacetFiltersForm.renderProductCount(html);
			});
	}

	static renderSectionFromCache(filterDataUrl, event) {
		const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
		FacetFiltersForm.renderFilters(html, event);
		FacetFiltersForm.renderSortBy(html);
		FacetFiltersForm.renderProductGridContainer(html);
		FacetFiltersForm.renderProductCount(html);
	}

	static renderProductGridContainer(html) {
		document.querySelector('[data-facets-products-grid]').innerHTML =
			new DOMParser()
				.parseFromString(html, 'text/html')
				.querySelector('[data-facets-products-grid]').innerHTML;

		// Dispatch event for datalayer re-init
		document.dispatchEvent(new CustomEvent('facets:rendered'));
	}

	static getActiveSortBy() {
		const fromUrl = new URLSearchParams(window.location.search).get('sort_by');
		if (fromUrl) return fromUrl;

		if (FacetFiltersForm.searchParamsPrev) {
			return new URLSearchParams(FacetFiltersForm.searchParamsPrev).get(
				'sort_by',
			);
		}

		return null;
	}

	// Syncs radios with ?sort_by= when present; otherwise keeps server markup (e.g. after clear all)
	static syncSortCheckedFromUrl() {
		const sortBy = FacetFiltersForm.getActiveSortBy();

		if (sortBy) {
			document.querySelectorAll('[data-facets-sort-by]').forEach((container) => {
				const placement = container.dataset.facetsSortBy;
				const inputName = FacetFiltersForm.getSortInputName(placement);

				container
					.querySelectorAll(`input[name="${inputName}"]`)
					.forEach((input) => {
						input.checked = input.value === sortBy;
					});
			});
		}

		FacetsSortSelect.syncAllUiState();
	}

	// Only the active viewport sort group is enabled for submission
	static syncSortControlsState() {
		const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
		const sidebarSort = document.querySelector('[data-facets-sort-by="sidebar"]');
		const desktopSort = document.querySelector('[data-facets-sort-by="desktop"]');

		sidebarSort
			?.querySelectorAll(
				`[name="${FacetFiltersForm.SORT_INPUT_NAME.sidebar}"]`,
			)
			.forEach((control) => {
				control.disabled = isDesktop;
			});

		desktopSort
			?.querySelectorAll(
				`[name="${FacetFiltersForm.SORT_INPUT_NAME.desktop}"]`,
			)
			.forEach((control) => {
				control.disabled = !isDesktop;
			});
	}

	// Replaces mobile + desktop sort markup from the section HTML response
	static renderSortBy(html) {
		const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

		parsedHTML.querySelectorAll('[data-facets-sort-by]').forEach((source) => {
			const target = document.querySelector(
				`[data-facets-sort-by="${source.dataset.facetsSortBy}"]`,
			);

			if (!target) return;

			if (source.dataset.facetsSortBy === 'desktop') {
				const sourceSelect = source.querySelector('facets-sort-select');
				const targetSelect = target.querySelector('facets-sort-select');

				if (sourceSelect && targetSelect) {
					targetSelect.replaceWith(document.importNode(sourceSelect, true));
				} else {
					target.innerHTML = source.innerHTML;
				}
			} else {
				target.innerHTML = source.innerHTML;
			}
		});

		FacetFiltersForm.syncSortCheckedFromUrl();
		FacetFiltersForm.syncSortControlsState();
		FacetsSortSelect.refreshAll();
	}

	static renderProductCount(html) {
		const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

		const nextCountNumber =
			parsedHTML.querySelector('[data-facets-count-number]')?.innerHTML ?? '';
		const nextCountLabel =
			parsedHTML.querySelector('[data-facets-count-label]')?.innerHTML ??
			nextCountNumber;

		document
			.querySelectorAll('[data-facets-count-number]')
			.forEach((container) => {
				container.innerHTML = nextCountNumber;
			});

		document
			.querySelectorAll('[data-facets-count-label]')
			.forEach((container) => {
				container.innerHTML = nextCountLabel;
			});
	}

	static renderFilters(html, event) {
		const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

		const facetDetailsElements = parsedHTML.querySelectorAll(
			'[data-facets-form] [data-facets-filter]',
		);
		const matchesIndex = (element) => {
			const jsFilter = event
				? event.target.closest('[data-facets-filter]')
				: undefined;
			return jsFilter
				? element.dataset.index === jsFilter.dataset.index
				: false;
		};
		const facetsToRender = Array.from(facetDetailsElements).filter(
			(element) => !matchesIndex(element),
		);
		facetsToRender.forEach((element) => {
			document.querySelector(
				`[data-index="${element.dataset.index}"]`,
			).innerHTML = element.innerHTML;
		});

		FacetFiltersForm.renderActiveFacets(parsedHTML);
	}

	static renderActiveFacets(html) {
		const activeFacetElementSelectors = ['[data-facets-actives]'];

		activeFacetElementSelectors.forEach((selector) => {
			const activeFacetsElement = html.querySelector(selector);
			if (!activeFacetsElement) return;
			document.querySelector(selector).innerHTML =
				activeFacetsElement.innerHTML;
		});

		FacetFiltersForm.toggleActiveFacets(false);
	}

	static updateURLHash(searchParams) {
		history.pushState(
			{searchParams},
			'',
			`${window.location.pathname}${searchParams && '?'.concat(searchParams)}`,
		);
	}

	static getSections() {
		return [
			{
				section: document.querySelector('[data-facets-product-grid]').dataset
					.id,
			},
		];
	}

	createSearchParams(form) {
		const formData = new FormData(form);
		const searchParams = new URLSearchParams(formData);

		// Shopify expects sort_by; desktop radios use sort_by_desktop
		searchParams.delete(FacetFiltersForm.SORT_INPUT_NAME.desktop);
		const activeSort = FacetFiltersForm.getCheckedSortValue();
		if (activeSort) {
			searchParams.set('sort_by', activeSort);
		} else {
			searchParams.delete('sort_by');
		}

		const hasVariantOrPriceFilter =
			searchParams.get('filter.v.option') ||
			searchParams.get('filter.p.m') ||
			searchParams.get('filter.v.m') ||
			searchParams.get('filter.p.vendor') ||
			searchParams.get('filter.v.price.gte') ||
			searchParams.get('filter.v.price.lte');

		const hasAvailabilityFilter = searchParams.has('filter.v.availability');

		if (hasVariantOrPriceFilter && !hasAvailabilityFilter) {
			searchParams.append('filter.v.availability', '1');
		} else if (!hasVariantOrPriceFilter && hasAvailabilityFilter) {
			searchParams.delete('filter.v.availability');
		}

		FacetFiltersForm.normalizePriceFilterParams(searchParams);

		return searchParams.toString();
	}

	// filter.v.price.lte: ceil so products with cent-level prices are not excluded (Liquid divided_by floors)
	static normalizePriceFilterParams(searchParams) {
		const priceLte = searchParams.get('filter.v.price.lte');

		if (priceLte !== null && priceLte !== '') {
			searchParams.set(
				'filter.v.price.lte',
				String(Math.ceil(parseFloat(priceLte))),
			);
		}
	}

	onSubmitForm(searchParams, event) {
		FacetFiltersForm.renderPage(searchParams, event);
	}

	onSubmitHandler(event) {
		event.preventDefault();
		// FormData includes fields inside the form + form-associated desktop sort
		const facetForm = document.querySelector(
			'facet-filters-form [data-facets-form]#FacetFiltersForm',
		);

		if (!facetForm) return;

		this.onSubmitForm(this.createSearchParams(facetForm), event);
	}

	onActiveFilterClick(event) {
		event.preventDefault();
		FacetFiltersForm.toggleActiveFacets();
		const url =
			event.currentTarget.href.indexOf('?') == -1
				? ''
				: event.currentTarget.href.slice(
						event.currentTarget.href.indexOf('?') + 1,
					);
		FacetFiltersForm.renderPage(url);
	}
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
if (!customElements.get('facet-filters-form')) {
	customElements.define('facet-filters-form', FacetFiltersForm);
}
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
	constructor() {
		super();
		this.querySelectorAll('input[type="range"]').forEach((element) =>
			element.addEventListener('input', this.onRangeChange.bind(this)),
		);
		this.syncHiddenInputs();
		this.setMinAndMaxValues();
	}

	syncHiddenInputs() {
		const minInput = this.querySelector('[data-price-range-min]');
		const maxInput = this.querySelector('[data-price-range-max]');
		const realInputMin = this.querySelector('input[name="filter.v.price.gte"]');
		const realInputMax = this.querySelector('input[name="filter.v.price.lte"]');

		if (!minInput || !maxInput || !realInputMin || !realInputMax) return;

		realInputMin.value = minInput.value;
		realInputMax.value = String(Math.ceil(parseFloat(maxInput.value) || 0));
	}

	notifyFacetFormChange() {
		const hiddenInput = this.querySelector('input[name="filter.v.price.gte"]');
		hiddenInput?.dispatchEvent(new Event('input', {bubbles: true}));
	}

	onRangeChange(event) {
		const minInput = this.querySelector('[data-price-range-min]');
		const maxInput = this.querySelector('[data-price-range-max]');

		// Limiter la plage de prix à 50€
		const min = parseFloat(minInput.value);
		const max = parseFloat(maxInput.value);
		const diffMinMax = 1;
		if (max - min < diffMinMax) {
			if (event.currentTarget === minInput) {
				maxInput.value = min + diffMinMax;
			} else {
				minInput.value = max - diffMinMax;
			}
		}

		this.adjustToValidValues(event.currentTarget);
		this.syncHiddenInputs();
		this.setMinAndMaxValues();
		this.updateTrackColors();
		this.notifyFacetFormChange();
	}

	setMinAndMaxValues() {
		const minActiveValueElement = this.querySelector('[data-min-active-value]');
		const maxActiveValueElement = this.querySelector('[data-max-active-value]');
		const currency = this.getAttribute('data-currency');
		const inputs = this.querySelectorAll('input[type="range"]');
		const minInput = inputs[0];
		const maxInput = inputs[1];

		if (minInput.value)
			minActiveValueElement.textContent = `${minInput.value}${currency}`;
		if (maxInput.value)
			maxActiveValueElement.textContent = `${maxInput.value}${currency}`;

		this.updateTrackColors(); // Appel pour mettre à jour les couleurs
	}

	adjustToValidValues(input) {
		const value = parseFloat(input.value);
		const min = parseFloat(input.getAttribute('min'));
		const max = parseFloat(input.getAttribute('max'));

		if (value < min) input.value = min;
		if (value > max) input.value = max;
	}

	updateTrackColors() {
		const track = this.querySelector('.c-facets__price');
		const minInput = this.querySelector('[data-price-range-min]');
		const maxInput = this.querySelector('[data-price-range-max]');
		if (!track || !minInput || !maxInput) return;

		const min = parseFloat(minInput.value);
		const max = parseFloat(maxInput.value);
		const rangeMin = parseFloat(minInput.getAttribute('min'));
		const rangeMax = parseFloat(maxInput.getAttribute('max'));

		const minPercentage = ((min - rangeMin) / (rangeMax - rangeMin)) * 100;
		const maxPercentage = ((max - rangeMin) / (rangeMax - rangeMin)) * 100;

		track.style.setProperty('--range-min', `${minPercentage}%`);
		track.style.setProperty('--range-max', `${maxPercentage}%`);
	}
}

if (!customElements.get('price-range')) {
	customElements.define('price-range', PriceRange);
}

/** Desktop faux-select: button + panel; real inputs are radios (facets AJAX unchanged). */
class FacetsSortSelect extends HTMLElement {
	connectedCallback() {
		this.init();
	}

	disconnectedCallback() {
		this.destroy();
	}

	// Safe to call after AJAX DOM swaps (see FacetFiltersForm.renderSortBy)
	static refreshAll() {
		document.querySelectorAll('facets-sort-select').forEach((element) => {
			customElements.upgrade(element);

			if (element instanceof FacetsSortSelect) {
				element.init();
			}
		});
	}

	static syncAllUiState() {
		document.querySelectorAll('facets-sort-select').forEach((element) => {
			if (element instanceof FacetsSortSelect) {
				element.syncLabel();
				element.syncOptionsState();
			}
		});
	}

	init() {
		this.destroy();

		this.sortInputName =
			this.dataset.facetsSortInputName ||
			FacetFiltersForm.SORT_INPUT_NAME.desktop;
		this.trigger = this.querySelector('[data-facets-sort-trigger]');
		this.panel = this.querySelector('[data-facets-sort-panel]');
		this.labelEl = this.querySelector('[data-facets-sort-label]');

		this.onTriggerClick = (event) => {
			event.stopPropagation();
			this.toggle();
		};
		this.onDocumentClick = () => {
			this.setOpen(false);
		};
		this.onKeydown = (event) => {
			if (event.key === 'Escape') {
				this.setOpen(false);
			}
		};
		// Delegated: survives radio markup replacement inside the panel
		this.onChange = (event) => {
			if (event.target.name !== this.sortInputName) return;

			this.syncLabel();
			this.syncOptionsState();
			this.setOpen(false);
		};

		this.trigger?.addEventListener('click', this.onTriggerClick);
		this.addEventListener('change', this.onChange);

		this.syncLabel();
		this.syncOptionsState();
	}

	destroy() {
		this.setOpen(false);
		this.trigger?.removeEventListener('click', this.onTriggerClick);
		this.removeEventListener('change', this.onChange);
	}

	toggle() {
		this.setOpen(this.trigger?.getAttribute('aria-expanded') !== 'true');
	}

	setOpen(isOpen) {
		if (isOpen) {
			if (!this.trigger || !this.panel) return;

			this.trigger.setAttribute('aria-expanded', 'true');
			this.panel.hidden = false;
			document.addEventListener('click', this.onDocumentClick);
			document.addEventListener('keydown', this.onKeydown);
			return;
		}

		document.removeEventListener('click', this.onDocumentClick);
		document.removeEventListener('keydown', this.onKeydown);

		if (this.trigger && this.panel) {
			this.trigger.setAttribute('aria-expanded', 'false');
			this.panel.hidden = true;
		}
	}

	syncLabel() {
		const checked = this.querySelector(
			`input[name="${this.sortInputName}"]:checked`,
		);
		if (!checked || !this.labelEl) return;

		const optionLabel = this.querySelector(`label[for="${checked.id}"]`);
		if (optionLabel) {
			this.labelEl.textContent = optionLabel.textContent.trim();
		}
	}

	syncOptionsState() {
		this.querySelectorAll('[role="option"]').forEach((option) => {
			const input = option.querySelector(`input[name="${this.sortInputName}"]`);
			option.setAttribute('aria-selected', input?.checked ? 'true' : 'false');
		});
	}
}

if (!customElements.get('facets-sort-select')) {
	customElements.define('facets-sort-select', FacetsSortSelect);
}

FacetFiltersForm.syncSortCheckedFromUrl();
FacetFiltersForm.syncSortControlsState();
FacetsSortSelect.refreshAll();

class FacetRemove extends HTMLElement {
	constructor() {
		super();
		const facetLink = this.querySelector('a');
		facetLink.setAttribute('role', 'button');
		facetLink.addEventListener('click', this.closeFilter.bind(this));
		facetLink.addEventListener('keyup', (event) => {
			if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
		});
	}

	closeFilter(event) {
		event.preventDefault();
		const form = this.closest('facet-filters-form');
		form.onActiveFilterClick(event);
	}
}

if (!customElements.get('facet-remove')) {
	customElements.define('facet-remove', FacetRemove);
}
